import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ListChecks, Plus, X, Trash2, Repeat, CalendarClock, Check, Lock } from 'lucide-react';
import { getPropertyTasks, createPropertyTask, deletePropertyTask } from '../lib/api';
import { toast } from 'sonner';

const todayStr = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

const QUICK = ['New laundry', 'Bins out for the council', 'Restock supplies', 'Deep clean'];
const FREQ = [
  { v: 'weekly', l: 'Every week' },
  { v: 'biweekly', l: 'Every 2 weeks' },
  { v: 'monthly', l: 'Monthly' },
];

const summary = (t) => {
  if (t.type === 'recurring') {
    const f = FREQ.find((x) => x.v === t.frequency)?.l || 'Repeats';
    return `${f} · from ${t.date} · ${t.time}`;
  }
  return `${t.date} · ${t.time}`;
};

// Reusable tasks editor. Live mode when propertyId is set; staged mode via value/onChange (used before a property exists).
export default function PropertyTasksEditor({ propertyId = null, value = [], onChange, locked = false }) {
  const live = !!propertyId;
  const [tasks, setTasks] = useState(live ? [] : value);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', type: 'once', date: todayStr(), time: '11:00', frequency: 'weekly', endDate: '' });

  useEffect(() => {
    if (live) {
      getPropertyTasks(propertyId).then((r) => setTasks(r.data.tasks || [])).catch(() => {});
    }
  }, [live, propertyId]);

  useEffect(() => { if (!live) onChange?.(tasks); }, [tasks, live]);

  const reset = () => setForm({ title: '', type: 'once', date: todayStr(), time: '11:00', frequency: 'weekly', endDate: '' });

  const add = async () => {
    const title = form.title.trim().slice(0, 50);
    if (!title) { toast.error('Add a task title'); return; }
    const payload = {
      title,
      type: form.type,
      date: form.date,
      time: form.time,
      frequency: form.type === 'recurring' ? form.frequency : undefined,
      endDate: form.type === 'recurring' && form.endDate ? form.endDate : undefined,
    };
    if (live) {
      setBusy(true);
      try {
        const r = await createPropertyTask(propertyId, payload);
        setTasks((p) => [...p, r.data.task]);
        toast.success('Task added');
        reset(); setAdding(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to add task');
      } finally { setBusy(false); }
    } else {
      setTasks((p) => [...p, { ...payload, id: `tmp-${Date.now()}` }]);
      reset(); setAdding(false);
    }
  };

  const remove = async (id) => {
    if (live && !String(id).startsWith('tmp-')) {
      try { await deletePropertyTask(propertyId, id); } catch (e) { toast.error('Failed to remove'); return; }
    }
    setTasks((p) => p.filter((t) => t.id !== id));
  };

  return (
    <div data-testid="property-tasks-editor">
      <div className="flex items-center gap-2 mb-2">
        <ListChecks size={16} className="text-orange-500" />
        <label className="text-sm font-semibold text-slate-700">Scheduled tasks</label>
      </div>
      <p className="text-xs text-slate-500 mb-3">Recurring or one-off jobs for the Team. They will see the tasks on Home on the right day.</p>

      {locked && (
        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="tasks-locked-notice">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Lock size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Available on Growth and above</p>
            <p className="text-xs text-slate-500 mt-0.5">Upgrade your plan to schedule recurring &amp; one-off tasks for your team.</p>
          </div>
        </div>
      )}

      {!locked && (<>

      {tasks.length > 0 && (
        <div className="space-y-2 mb-3">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2" data-testid={`task-item-${t.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  {t.type === 'recurring' ? <Repeat size={14} className="text-orange-600" /> : <CalendarClock size={14} className="text-orange-600" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.title}</p>
                  <p className="text-xs text-slate-500 truncate">{summary(t)}</p>
                </div>
              </div>
              <button type="button" onClick={() => remove(t.id)} className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center flex-shrink-0 group" data-testid={`task-delete-${t.id}`}>
                <Trash2 size={14} className="text-slate-400 group-hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={() => setAdding(true)} data-testid="add-task-button"
        className="w-full h-11 rounded-xl border-2 border-dashed border-orange-300 text-orange-600 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-orange-50 transition-colors">
        <Plus size={16} /> Add task
      </button>

      {createPortal(
        <AnimatePresence>
          {adding && (
          <motion.div key="task-modal-overlay" className="fixed inset-0 z-[110] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => { reset(); setAdding(false); }} />
            <motion.div initial={{ y: 40, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0 }}
              className="relative w-full sm:max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[88vh] overflow-y-auto" data-testid="task-modal">
              <div className="px-5 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white flex items-center justify-between sticky top-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center"><ListChecks size={18} /></div>
                  <div>
                    <h3 className="font-bold text-base leading-tight">Add task</h3>
                    <p className="text-xs text-white/80">One-off or recurring job for the team</p>
                  </div>
                </div>
                <button type="button" aria-label="Close" data-testid="task-modal-close" onClick={() => { reset(); setAdding(false); }} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16} /></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <input type="text" value={form.title} maxLength={50} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Task title (max 50)" data-testid="task-title-input"
                    className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {QUICK.map((q) => (
                      <button key={q} type="button" onClick={() => setForm({ ...form, title: q })}
                        className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-white border border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600 transition-colors">{q}</button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setForm({ ...form, type: 'once' })} data-testid="task-type-once"
                    className={`h-10 rounded-xl text-sm font-semibold border transition-colors ${form.type === 'once' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200'}`}>One-off</button>
                  <button type="button" onClick={() => setForm({ ...form, type: 'recurring' })} data-testid="task-type-recurring"
                    className={`h-10 rounded-xl text-sm font-semibold border transition-colors ${form.type === 'recurring' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200'}`}>Recurring</button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">{form.type === 'recurring' ? 'Starts' : 'Date'}</label>
                    <input type="date" value={form.date} min={todayStr()} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="task-date"
                      className="w-full h-11 rounded-xl border border-slate-200 px-2.5 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Time</label>
                    <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="task-time"
                      className="w-full h-11 rounded-xl border border-slate-200 px-2.5 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-400 outline-none" />
                  </div>
                </div>

                {form.type === 'recurring' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Repeat</label>
                      <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="task-frequency"
                        className="w-full h-11 rounded-xl border border-slate-200 px-2.5 text-sm font-semibold text-slate-700 bg-white focus:ring-2 focus:ring-orange-400 outline-none">
                        {FREQ.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Ends <span className="normal-case font-medium text-slate-300">· optional</span></label>
                      <input type="date" value={form.endDate} min={form.date} onChange={(e) => setForm({ ...form, endDate: e.target.value })} data-testid="task-end"
                        className="w-full h-11 rounded-xl border border-slate-200 px-2.5 text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-orange-400 outline-none" />
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => { reset(); setAdding(false); }} className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Cancel</button>
                  <button type="button" onClick={add} disabled={busy} data-testid="task-save"
                    className="flex-1 h-11 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
                    <Check size={16} /> {busy ? 'Saving...' : 'Add task'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
      </>)}
    </div>
  );
}
